import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonSearchbar,
  IonCard,
  IonCardContent,
  IonButton
} from '@ionic/react';
import {
  helpCircle,
  book,
  code,
  trophy,
  settings,
  play,
  chatbubbles,
  mail,
  chevronDown
} from 'ionicons/icons';

import { configService } from '../services/ConfigService';
import MascotMessage from '../components/MascotMessage';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Help.css';

function Help() {
  const [searchText, setSearchText] = useState('');
  const baseMeta = seoService.getPageMeta('home');
  const [expandedId, setExpandedId] = useState(null);
  const config = configService.getConfig();
  
  // Get language name from config for dynamic content
  const languageName = config?.app?.language 
    ? config.app.language.charAt(0).toUpperCase() + config.app.language.slice(1)
    : 'the language';
  
  // Language-specific syntax tips
  const getSyntaxTips = () => {
    const lang = config?.app?.language?.toLowerCase();
    switch (lang) {
      case 'python':
        return 'Check for typos, missing colons, or incorrect indentation. Python is sensitive to spacing!';
      case 'java':
        return 'Check for typos, missing semicolons, or mismatched braces. Java requires exact syntax!';
      case 'javascript':
        return 'Check for typos, missing semicolons, or mismatched brackets. JavaScript can be tricky with types!';
      case 'cpp':
      case 'c':
        return 'Check for typos, missing semicolons, or mismatched braces. Memory management is important!';
      default:
        return 'Check for typos and syntax errors. The error message usually tells you which line has the problem.';
    }
  };

  const faqs = [
    {
      category: 'Getting Started',
      icon: book,
      questions: [
        {
          question: 'How do I start learning?',
          answer: 'Go to the "Learn" tab and select any lesson. Start from the beginning for the best experience! Each lesson walks you through concepts step-by-step.'
        },
        {
          question: 'Do I need any prior coding experience?',
          answer: 'Not at all! This app is designed for absolute beginners. We explain everything from scratch using simple language and visual examples.'
        },
        {
          question: 'How long does it take to complete all lessons?',
          answer: 'At your own pace! Most users complete all 50 lessons in about 3-4 weeks when studying 15-30 minutes daily.'
        }
      ]
    },
    {
      category: 'Code Editor',
      icon: code,
      questions: [
        {
          question: 'How do I run my code?',
          answer: 'Type your code in the editor and tap the green "Run" button. Results appear instantly below the editor!'
        },
        {
          question: 'Why is my code not working?',
          answer: getSyntaxTips() + ' The error message usually tells you which line has the problem.'
        },
        {
          question: 'Can I save my code?',
          answer: 'Yes! Code you write in the Playground is automatically saved. In lessons, your progress is saved when you complete each step.'
        }
      ]
    },
    {
      category: 'Gamification',
      icon: trophy,
      questions: [
        {
          question: 'How do I earn points?',
          answer: 'Complete lessons (+100 pts), solve challenges (+50 pts), answer quizzes correctly (+25 pts), and maintain daily streaks for bonus points!'
        },
        {
          question: 'What are badges?',
          answer: 'Badges are achievements you unlock by reaching milestones - completing lessons, maintaining streaks, solving challenges, and more!'
        },
        {
          question: 'How do daily streaks work?',
          answer: 'Complete at least one lesson or challenge each day to maintain your streak. The longer your streak, the more bonus points you earn!'
        }
      ]
    },
    {
      category: 'Code Playground',
      icon: play,
      questions: [
        {
          question: 'What can I do in the Playground?',
          answer: `Experiment freely! Write any ${languageName} code, try examples, or practice concepts from lessons. It's your sandbox to learn by doing.`
        },
        {
          question: 'Are there code templates?',
          answer: 'Yes! The Playground has pre-built templates for common patterns like loops, functions, and classes to help you get started.'
        }
      ]
    },
    {
      category: 'App Settings',
      icon: settings,
      questions: [
        {
          question: 'How do I enable dark mode?',
          answer: 'Go to Profile > Settings and toggle "Dark Mode" on. The app will switch to a dark theme that\'s easier on your eyes.'
        },
        {
          question: 'Can I reset my progress?',
          answer: 'Yes, but be careful! Go to Profile > Settings > Reset Progress. This clears all your lessons, badges, and points.'
        },
        {
          question: 'Does the app work offline?',
          answer: `Most features work offline! Lessons, quizzes, and ${languageName} code execution all work without internet. Some features like sharing may require connection.`
        }
      ]
    }
  ];

  // Filter FAQs based on search
  const filteredFaqs = searchText 
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(q => 
          q.question.toLowerCase().includes(searchText.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchText.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  const openSupport = () => {
    const email = config?.app?.supportEmail || 'support@example.com';
    const appName = config?.app?.name || `Learn ${languageName}`;
    window.open(`mailto:${email}?subject=Help Request - ${appName}`, '_system');
  };

  return (
    <>
      <SEO
        title="Help & Support"
        description="Get help with common questions, troubleshooting, and contact support."
        ogImage={baseMeta.ogImage}
      />
      <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile" />
          </IonButtons>
          <IonTitle>Help & FAQ</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="help-container">
          {/* Mascot Welcome */}
          <MascotMessage 
            message="Need help? I've got answers! 🤓"
            state="explain"
          />

          {/* Search */}
          <IonSearchbar
            value={searchText}
            onIonInput={(e) => setSearchText(e.detail.value)}
            placeholder="Search help topics..."
            className="help-search"
          />

          {/* Quick Help Cards */}
          <div className="quick-help">
            <h2>Quick Help</h2>
            <div className="quick-help-grid">
              <div className="quick-help-item" onClick={() => setSearchText('start')}>
                <IonIcon icon={book} />
                <span>Getting Started</span>
              </div>
              <div className="quick-help-item" onClick={() => setSearchText('code')}>
                <IonIcon icon={code} />
                <span>Code Help</span>
              </div>
              <div className="quick-help-item" onClick={() => setSearchText('points')}>
                <IonIcon icon={trophy} />
                <span>Points & Badges</span>
              </div>
              <div className="quick-help-item" onClick={openSupport}>
                <IonIcon icon={chatbubbles} />
                <span>Contact Us</span>
              </div>
            </div>
          </div>

          {/* FAQ Accordions */}
          <div className="faq-section">
            <h2>Frequently Asked Questions</h2>
            
            {filteredFaqs.length === 0 ? (
              <div className="no-results">
                <IonIcon icon={helpCircle} />
                <p>No matching questions found</p>
                <IonButton fill="clear" onClick={() => setSearchText('')}>
                  Clear Search
                </IonButton>
              </div>
            ) : (
              <div>
                {filteredFaqs.map((category, catIdx) => (
                  <div key={catIdx} className="faq-category">
                    <div className="category-header">
                      <IonIcon icon={category.icon} />
                      <span>{category.category}</span>
                    </div>
                    {category.questions.map((item, qIdx) => {
                      const itemId = `${catIdx}-${qIdx}`;
                      const isExpanded = expandedId === itemId;
                      return (
                        <div key={itemId} className="faq-item">
                          <div 
                            className={`faq-question-header ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => setExpandedId(isExpanded ? null : itemId)}
                          >
                            <span className="faq-question">{item.question}</span>
                            <IonIcon 
                              icon={chevronDown} 
                              className={`faq-chevron ${isExpanded ? 'rotated' : ''}`}
                            />
                          </div>
                          {isExpanded && (
                            <div className="faq-answer">
                              {item.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Support */}
          <IonCard className="support-card">
            <IonCardContent>
              <h3>Still need help?</h3>
              <p>Can't find what you're looking for? Contact our support team!</p>
              <IonButton expand="block" onClick={openSupport}>
                <IonIcon icon={mail} slot="start" />
                Contact Support
              </IonButton>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default Help;